import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { Button, Grid, TextField, CircularProgress } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppSwitch from "components/Shared/AppSwitch";
import { useGetCountryDDLQuery } from "store/services/countryApi";

const INITIAL_STATE = {
  name: "",
  scriptCode: "",
  country: null,
  url: "",
  isActive: true,
};

const SMS_PROVIDER_OPTIONS = [
  { label: "Sparrow SMS", value: "Sparrow SMS", scriptCode: "sparrowSMS" },
  { label: "Aakash SMS", value: "Aakash SMS", scriptCode: "aakashSMS" },
];

const SmsProviderForm = ({
  isBusy = false,
  smsProviderDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();

  // RTK Queries
  const {
    data: countries = [],
    isLoading: loadingCountries,
    isFetching: fetchingCountries,
  } = useGetCountryDDLQuery(
    { status: "Active" },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: INITIAL_STATE,
    mode: "onChange",
  });

  // Memoize mapped countries if necessary
  const _mappedCountries = useMemo(
    () =>
      Array.isArray(countries)
        ? countries.map((item) => ({
            label: item?.name || item?.label || "",
            _id: item?._id || "",
          }))
        : [],
    [countries]
  );

  // Handle Form Submission
  const handleOnSubmit = (data) => {
    const formattedData = {
      ...data,
      country: data?.country?._id,
    };
    onSubmit(formattedData);
  };

  // Reset form when smsProviderDetail changes
  const isMounted = useRef(true);
  useEffect(() => {
    if (!isMounted.current || loadingCountries || fetchingCountries) return;

    if (!smsProviderDetail) {
      reset(INITIAL_STATE);
      return;
    }

    const countryObj = countries.find(
      (c) =>
        c._id === (smsProviderDetail.country?._id || smsProviderDetail.country)
    );

    reset({
      ...INITIAL_STATE,
      ...smsProviderDetail,
      country: countryObj
        ? { _id: countryObj._id, label: countryObj.name || countryObj.label }
        : null,
    });

    return () => {
      isMounted.current = false;
    };
  }, [
    smsProviderDetail,
    reset,
    countries,
    loadingCountries,
    fetchingCountries,
  ]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Name is required." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  freeSolo
                  fullWidth
                  required
                  label="Name"
                  value={value || ""}
                  onChange={(_, newValue) => {
                    if (typeof newValue === "string") {
                      onChange(newValue);
                      const provider = SMS_PROVIDER_OPTIONS.find(
                        (option) => option.value === newValue
                      );
                      setValue("scriptCode", provider ? provider.scriptCode : "");
                    } else if (newValue && newValue.value) {
                      onChange(newValue.value);
                      setValue("scriptCode", newValue.scriptCode);
                    }
                  }}
                  options={SMS_PROVIDER_OPTIONS}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.label
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Name"
                      size="small"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="url"
              control={control}
              rules={{ required: "API URL is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="API URL"
                  size="small"
                  error={!!errors.url}
                  helperText={errors.url?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="country"
              control={control}
              rules={{ required: "Country is required." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Country"
                  value={value ?? null}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => option?.label ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={_mappedCountries}
                  loading={loadingCountries || fetchingCountries}
                  loadingText="Loading Countries..."
                  disabled={loadingCountries || fetchingCountries}
                  helperText={errors?.country?.message}
                  error={!!errors?.country}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      required
                      error={!!errors?.country}
                      helperText={errors?.country?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {(loadingCountries || fetchingCountries) && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>
          {smsProviderDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSubmit)}
          disabled={isBusy || loadingCountries || fetchingCountries}
        >
          {smsProviderDetail ? "Update SMS Provider" : "Add SMS Provider"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default SmsProviderForm;